import { asky, baseApi, Http, rtkq, RtkqRequest } from '@app/base';
import {
  GetConsentDocument,
  GetConsentsCountDocument,
  GetConsentsDocument,
  GetConsentExportDataDocument,
} from './graphql';
import {
  CreateConsentRequest,
  ConsentListItem,
  Consent,
  ConsentExportData,
  ConsentStatus,
  SignConsentRequest,
  UpdateConsentRequest,
} from './consentsTypes';

export const ConsentsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getAllConsents: build.query<
      ConsentListItem[],
      RtkqRequest<{
        assigneeId?: string;
        isMandatory?: boolean;
        statuses?: ConsentStatus[];
        referenceId?: string;
      }>
    >({
      queryFn: rtkq.graphql(GetConsentsDocument, {
        select: (x) => x.consents as ConsentListItem[],
        vars: ({ assigneeId, isMandatory, statuses, referenceId }) => {
          const filter = asky.filter.json([
            !!assigneeId && { fieldId: 'assigneeId', operator: '=', value: assigneeId },
            !!isMandatory && { fieldId: 'isMandatory', operator: '=', value: isMandatory },
            !!statuses && { fieldId: 'status', operator: 'in', values: statuses },
            !!referenceId && { fieldId: 'referenceId', operator: 'contains', value: referenceId },
          ]);

          return { filter };
        },
      }),
      providesTags: ['main:consent'],
    }),

    getConsent: build.query<Consent, RtkqRequest<{ id: string }>>({
      queryFn: rtkq.graphql(GetConsentDocument, {
        select: (x) => x.consent! as Consent,
        vars: ({ id }) => ({ id }),
      }),
      providesTags: ['main:consent'],
    }),

    getConsentExportData: build.query<
      ConsentExportData,
      RtkqRequest<{ id: string; tenantId: string; assigneeId: string }>
    >({
      queryFn: rtkq.graphql(GetConsentExportDataDocument, {
        select: (x) => x as ConsentExportData,
        vars: ({ id, tenantId, assigneeId }) => ({ id, tenantId, assigneeId }),
      }),
      providesTags: ['main:consent'],
    }),

    getConsentsCount: build.query<
      number,
      RtkqRequest<{
        assigneeIds: string[];
        statuses: ConsentStatus[];
      }>
    >({
      queryFn: rtkq.graphql(GetConsentsCountDocument, {
        vars: ({ statuses, assigneeIds }) => {
          const filter = asky.filter.json([
            !!statuses && { fieldId: 'status', operator: 'in', values: statuses },
            !!assigneeIds && { fieldId: 'assigneeId', operator: 'in', values: assigneeIds },
          ]);

          return { filter };
        },
        select: (x) => x.consentsCount!,
      }),
      providesTags: ['main:consent'],
    }),

    createConsent: build.mutation<string, RtkqRequest<CreateConsentRequest>>({
      queryFn: rtkq.axios((args) => Http.post('/api/consent', args)),
      invalidatesTags: ['main:consent'],
    }),

    signConsent: build.mutation<void, RtkqRequest<SignConsentRequest>>({
      queryFn: rtkq.axios((args) => Http.put(`/api/consent/sign/${args.id}`, args)),
      invalidatesTags: ['main:consent'],
    }),

    updateConsent: build.mutation<void, RtkqRequest<UpdateConsentRequest>>({
      queryFn: rtkq.axios((args) => Http.put(`/api/consent/${args.id}`, args)),
      invalidatesTags: ['main:consent'],
    }),

    deleteConsent: build.mutation<string, RtkqRequest<{ id: string }>>({
      queryFn: rtkq.axios((args) => Http.delete(`/api/consent/${args.id}`)),
      invalidatesTags: ['main:consent'],
    }),
  }),
});

export interface UseUnsignedConsentsOptions {
  assigneeId?: string;
  isMandatory?: boolean;
  referenceId?: string;
}

export function useUnsignedConsents(options: UseUnsignedConsentsOptions) {
  const { assigneeId, isMandatory, referenceId } = options;
  const { data: consents, isLoading, isFetching, refetch } = useGetAllConsentsQuery(
    { assigneeId, isMandatory, referenceId, statuses: [ConsentStatus.NOT_SIGNED] },
    { skip: !assigneeId },
  );

  return { consents, isLoading, isFetching, refetch };
}

export const {
  useGetAllConsentsQuery,
  useGetConsentQuery,
  useLazyGetConsentExportDataQuery,
  useGetConsentsCountQuery,
  useCreateConsentMutation,
  useSignConsentMutation,
  useDeleteConsentMutation,
  useUpdateConsentMutation,
} = ConsentsApi;
